import * as React from 'react';
/**
 * @interface InputProps
 * @description Define las propiedades del componente Input.
 */
interface InputProps {
  /**
   * @property {string} label - El label del componente Input.
   */
  label?: string;
  /**
   * @property {string} type - El tipo del componente Input.
   */
  type?: 'text' | 'password' | 'email' | 'number' | 'tel' | 'url' | 'search' | 'date' | 'time' | 'datetime-local' | 'month' | 'week' | 'file';
  /**
   * @property {string} value - El valor del componente Input.
   */
  value?: string | number;
  /**
   * @property {function} onChange - La función de cambio del componente Input.
   */
  onChange?: (e: React.ChangeEvent<HTMLInputElement>) => void;
  /**
   * @property {boolean} required - Indica si el componente Input es requerido.
   */
  required?: boolean;
  /**
   * @property {string} error - El error del componente Input.
   */
  error?: string;
  /**
   * @property {string} helperText - El helper text del componente Input.
   */
  helperText?: string;
  /**
   * @property {React.ReactNode} leftIcon - El icono izquierdo del componente Input.
   */
  leftIcon?: React.ReactNode;
  /**
   * @property {React.ReactNode} rightIcon - El icono derecho del componente Input.
   */
  rightIcon?: React.ReactNode;
  /**
   * @property {string} className - La clase del componente Input.
   */
  className?: string;
  /**
   * @property {string} id - El id del componente Input.
   */
  id?: string;
  /**
   * @property {boolean} readOnly - Indica si el componente Input es de solo lectura.
   */
  readOnly?: boolean;
  /**
   * @property {string} placeholder - El placeholder del componente Input.
   */
  placeholder?: string;
  /**
   * @property {string} accept - Tipos de archivo aceptados (solo para type="file").
   */
  accept?: string;
  /**
   * @property {boolean} multiple - Permite selección múltiple de archivos (solo para type="file").
   */
  multiple?: boolean;
  /**
   * @property {boolean} disabled - Indica si el componente Input está deshabilitado.
   */
  disabled?: boolean;
}



/**
 * @component Input
 * @description Un componente de entrada personalizable con soporte para etiquetas, errores, texto de ayuda e iconos.
 * Soporta diferentes tipos de input incluyendo archivos (file).
 * @param {InputProps} props Las propiedades del componente.
 * @returns {React.ReactElement} El componente de entrada renderizado.
 */
export const Input = React.forwardRef<HTMLInputElement, InputProps>(({
  label,
  error,
  helperText,
  type = 'text',
  value,
  onChange,
  required = false,
  leftIcon,
  rightIcon,
  className = '',
  id,
  readOnly = false,
  accept,
  multiple,
  disabled = false,
  ...props
}, ref) => {
  const inputId = id || `input-${Math.random().toString(36).substr(2, 9)}`;
  const isFileType = type === 'file';

  // Para inputs de tipo file, no se usa value (por seguridad del navegador)
  const inputProps: React.InputHTMLAttributes<HTMLInputElement> = {
    id: inputId,
    type,
    onChange,
    required,
    readOnly,
    disabled,
    accept: isFileType ? accept : undefined,
    multiple: isFileType && multiple ? multiple : undefined,
    ...props,
  };

  // Solo agregar value si no es tipo file
  if (!isFileType && value !== undefined) {
    inputProps.value = value;
  }

  return (
    <div className="w-full">
      {label && (
        <label
          htmlFor={inputId}
          className="block text-sm font-medium text-gray-700 mb-1"
        >
          {label}
        </label>
      )}
      <div className="relative">
        {leftIcon && !isFileType && (
          <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
            {leftIcon}
          </div>
        )}
        <input
          ref={ref}
          {...inputProps}
          className={`
            block w-full px-3 py-2 border rounded-md shadow-sm placeholder-gray-400 ${type === 'password' ? 'pr-10' : ''}
            focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500
            ${
              error
                ? 'border-error-300 focus:ring-error-500 focus:border-error-500'
                : 'border-gray-300'
            }
            ${leftIcon && !isFileType ? 'pl-10' : ''}
            ${rightIcon && !isFileType ? 'pr-10' : ''}
            ${readOnly ? 'bg-gray-100 cursor-not-allowed' : ''}
            ${disabled ? 'bg-gray-100 cursor-not-allowed opacity-50' : ''}
            ${className}
          `}
        />
        {rightIcon && !isFileType && (
          <div className="absolute inset-y-0 right-0 pr-3 flex items-center pointer-events-none">
            {rightIcon}
          </div>
        )}
      </div>
      {error && <p className="mt-1 text-sm text-error-600">{error}</p>}
      {helperText && !error && (
        <p className="mt-1 text-sm text-gray-500">{helperText}</p>
      )}
    </div>
  );
});

Input.displayName = 'Input';
